import { authSplitApi } from 'redux/helpers/slice.helpers';
import { EPropertyMangerUrl } from 'data/urls/propertyManager.url';
import { EResidenceUrl } from 'data/urls/residence.url';
import {
  TPageableDataParamUI,
  TPageableDataWithContent,
  TPageableDataWithContentUi,
} from 'data/types/pagableData.type';
import {
  TAnimalBreedDB,
  TAnimalBreedUI,
  TAnimalColorsDB,
  TAnimalColorsUI,
  TAnimalDB,
  TAnimalDetail,
  TAnimalDetailUi,
  TAnimalSpecieDB,
  TAnimalSpecieUI,
  TAnimalsStatisticsDB,
  TAnimalsStatisticsUI,
  TAnimalTerminateDB,
  TAnimalTerminateUI,
  TAnimalUI,
  TAnimalVetRecordCreateUi,
  TCheckDuplicateAnimalUI,
  TConditionalMovedInApprovedUI,
  TCreateAnimalResponseDB,
  TCreateAnimalResponseUI,
  TCreateAnimalUI,
  TDownloadAnimalDocumnetsUI,
  TDuplicateAnimalDB,
  TDuplicateAnimalUI,
  TResidentAnimalScreeningUI,
  TUpdateAnimalAggressiveBehaviorUI,
  TUpdateAnimalBirthDateUI,
  TUpdateAnimalBreedUI,
  TUpdateAnimalColorsUI,
  TUpdateAnimalGenderUI,
  TUpdateAnimalHouseTrainedUI,
  TUpdateAnimalMicrochipDateUI,
  TUpdateAnimalNameUI,
  TUpdateAnimalProfilePictureUI,
  TUpdateAnimalReproductiveUI,
  TUpdateAnimalSpeciesUI,
  TUpdateAnimalTrainedForUI,
  TUpdateAnimalWeightUI,
  TVetRecordDeleteUI,
  TVetRecordDocumentsUI,
  TCreateAnimalIncompleteResponseDB,
  TCreateAnimalIncompleteResponseUI,
  TCreateAnimalIncompleteUI,
  TCompleteAnimalUI,
} from 'data/types/animals.types';
import { queryParamConverter } from 'data/converters/general.converters';
import { pageableDataConverter } from 'data/converters/pagableData.converters';
import {
  animalBreedConvertor,
  animalColorConvertor,
  animalConverter,
  animalSpecieConvertor,
  animalTerminateConverter,
  conditionalMovedInApproveConverter,
  createAnimalConvertor,
  createAnimalResponseConverter,
  deleteVetRecordConvertor,
  downloadAnimalDocumnetsParamsConverter,
  duplicateAnimalConverter,
  getAnimalConverter,
  getAnimalStatisticsConverter,
  residentAnimalScreening,
  updateAnimalAggressiveBehaviorConvertor,
  updateAnimalBirthDateConvertor,
  updateAnimalBreedConvertor,
  updateAnimalColorsConvertor,
  updateAnimalGenderConvertor,
  updateAnimalHouseTrainedConvertor,
  updateAnimalMicrochipConvertor,
  updateAnimalNameConvertor,
  updateAnimalProfilePictureConvertor,
  updateAnimalReproductiveConvertor,
  updateAnimalSpeciesConvertor,
  updateAnimalTrainedForConvertor,
  updateAnimalWeightConvertor,
  updateVaccinationRecordsConverter,
  incompleteAnimalConvertor,
  incompleteAnimalResponseConvertor,
  completeAnimalConverter,
} from 'data/converters/animals.converter';
import { downloadFile } from 'helpers/fileServices';
import { ETenantUrl } from 'data/urls/tenant.url';
import { EAnimalUrl } from 'data/urls/animal.url';
import { ECommonUrl } from 'data/urls/common.url';
import {
  TAllowedAnimalActionsDB,
  TAllowedAnimalActionsParamsUI,
  TAllowedAnimalActionsUI,
} from 'data/types/animalActions.types';
import { allowedAnimalActionsConverter } from 'data/converters/animalActions.converter';
import toast from 'components/Notification';
import { EText } from 'data/datas/genericData';

const baseUrl = `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EResidenceUrl.RESIDENCE}`;
const animalUrl = `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EAnimalUrl.ANIMALS}`;

export const animalApi = authSplitApi('animalApi', [
  'animal',
  'animals',
  'terminatedAnimals',
  'dashboardStatistics',
  'dashboardStatisticsNotChargedPets',
]).injectEndpoints({
  endpoints: (build) => ({
    getAllAnimals: build.query<TPageableDataWithContentUi<TAnimalUI[]>, TPageableDataParamUI>({
      query({ search, pagination }) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EResidenceUrl.ANIMALS}/${EResidenceUrl.ALL}`,
          method: 'GET',
          params: queryParamConverter(search, pagination, {
            // hidePendingAnimalsBeingApproved: 'true',
          }),
        };
      },
      transformResponse: (data: TPageableDataWithContent<TAnimalDB[]>) => {
        const { content, ...other } = data;
        return {
          content: content.map((item) => {
            return animalConverter.fromDb(item);
          }),
          paginationData: pageableDataConverter.fromDb(other),
        };
      },
      providesTags: ['animals'],
    }),
    getAnimal: build.query<TAnimalDetailUi, string>({
      query(id) {
        return {
          url: `${baseUrl}/${EResidenceUrl.ANIMAL}/${id}`,
          method: 'GET',
        };
      },
      providesTags: ['animal'],
      transformResponse: (data: TAnimalDetail) => getAnimalConverter.fromDb(data),
    }),
    getAnimalStatistics: build.query<TAnimalsStatisticsUI, void>({
      query() {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EResidenceUrl.ANIMALS}/${EResidenceUrl.STATISTICS}/${EResidenceUrl.DASHBOARD}`,
          method: 'GET',
        };
      },
      transformResponse: (data: TAnimalsStatisticsDB) => getAnimalStatisticsConverter.fromDb(data),
    }),
    terminateAnimal: build.mutation<TAnimalTerminateDB, TAnimalTerminateUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EResidenceUrl.ANIMALS}/${EResidenceUrl.TERMINATE}`,
          method: 'PATCH',
          body: animalTerminateConverter.toDb(body),
        };
      },
      invalidatesTags: ['animal', 'animals', 'terminatedAnimals'],
    }),
    allAnimalsExportCSV: build.query<string, TPageableDataParamUI & { format?: 'CSV' | 'XLSX'; columns?: string[] }>({
      query({ search, pagination, format = 'CSV', columns }) {
        const ext = format.toLowerCase();
        const extras: Record<string, string> = {
          hidePendingAnimalsBeingApproved: 'true',
          format,
        };
        if (columns?.length) {
          extras.columns = columns.join(',');
        }
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EResidenceUrl.ANIMALS}/${EResidenceUrl.ALL}/${EResidenceUrl.EXPORT}`,
          method: 'GET',
          params: queryParamConverter(search, pagination, extras),
          responseHandler: async (response) => {
            const file = await response.blob();
            downloadFile(file, `all animals.${ext}`);
            return null;
          },
        };
      },
    }),
    checkDuplicateAnimal: build.query<TDuplicateAnimalUI | null, TCheckDuplicateAnimalUI>({
      query(params) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EResidenceUrl.ANIMALS}/${EResidenceUrl.DUPLICATIONS}`,
          method: 'GET',
          params: { ...params },
        };
      },
      transformResponse: (data: TDuplicateAnimalDB[]) => {
        return data.length ? duplicateAnimalConverter.fromDb(data[0]) : null;
      },
    }),
    getAnimalSpecies: build.query<TAnimalSpecieUI[], void>({
      query() {
        return {
          url: `${ETenantUrl.TENANT}/${EResidenceUrl.ANIMALS}/${EResidenceUrl.SPECIES}`,
          method: 'GET',
        };
      },
      transformResponse: (data: TAnimalSpecieDB[]) => {
        return data.map((item) => animalSpecieConvertor.fromDb(item));
      },
    }),
    getAnimalColors: build.query<TAnimalColorsUI[], void>({
      query() {
        return {
          url: `${ETenantUrl.TENANT}/${EResidenceUrl.ANIMALS}/${EResidenceUrl.COLORS}`,
          method: 'GET',
        };
      },
      transformResponse: (data: TAnimalColorsDB[]) => {
        return data.map((item) => animalColorConvertor.fromDb(item));
      },
    }),
    getAnimalBreed: build.query<TAnimalBreedUI[], { speciesId: string }>({
      query(params) {
        return {
          url: `${ETenantUrl.TENANT}/${EResidenceUrl.ANIMALS}/${EResidenceUrl.BREEDS}`,
          method: 'GET',
          params,
        };
      },
      transformResponse: (data: TAnimalBreedDB[]) => {
        return data.map((item) => animalBreedConvertor.fromDb(item));
      },
    }),
    createAniaml: build.mutation<TCreateAnimalResponseUI, TCreateAnimalUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EResidenceUrl.ANIMAL}`,
          method: 'PATCH',
          body: createAnimalConvertor.toDb(body),
        };
      },
      transformResponse: (data: TCreateAnimalResponseDB) => {
        return createAnimalResponseConverter.fromDb(data);
      },
    }),

    updateAnimalName: build.mutation<void, TUpdateAnimalNameUI>({
      query(body) {
        return {
          url: `${animalUrl}/${EAnimalUrl.NAME}`,
          method: 'PATCH',
          body: updateAnimalNameConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalSpecies: build.mutation<void, TUpdateAnimalSpeciesUI>({
      query(body) {
        return {
          url: `${animalUrl}/${EAnimalUrl.SPECIES}`,
          method: 'PATCH',
          body: updateAnimalSpeciesConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalWeight: build.mutation<void, TUpdateAnimalWeightUI>({
      query(body) {
        return {
          url: `${animalUrl}/${EAnimalUrl.WEIGHT}`,
          method: 'PATCH',
          body: updateAnimalWeightConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalBirthDate: build.mutation<void, TUpdateAnimalBirthDateUI>({
      query(body) {
        return {
          url: `${animalUrl}/${EAnimalUrl.BIRTHDATE}`,
          method: 'PATCH',
          body: updateAnimalBirthDateConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalMicrochipDate: build.mutation<void, TUpdateAnimalMicrochipDateUI>({
      query(body) {
        return {
          url: `${animalUrl}/${EAnimalUrl.MICROCHIP}`,
          method: 'PATCH',
          body: updateAnimalMicrochipConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalReproductive: build.mutation<void, TUpdateAnimalReproductiveUI>({
      query(body) {
        return {
          url: `${animalUrl}/${EAnimalUrl.REPRODUCTIVE}`,
          method: 'PATCH',
          body: updateAnimalReproductiveConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalHouseTrained: build.mutation<void, TUpdateAnimalHouseTrainedUI>({
      query(body) {
        return {
          url: `${animalUrl}/${EAnimalUrl.HOUSE_TRAINED}`,
          method: 'PATCH',
          body: updateAnimalHouseTrainedConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalGender: build.mutation<void, TUpdateAnimalGenderUI>({
      query(body) {
        return {
          url: `${animalUrl}/${EAnimalUrl.GEDNER}`,
          method: 'PATCH',
          body: updateAnimalGenderConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalBreed: build.mutation<void, TUpdateAnimalBreedUI>({
      query(body) {
        return {
          url: `${animalUrl}/${EAnimalUrl.BREED}`,
          method: 'PATCH',
          body: updateAnimalBreedConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalAggressiveBehavior: build.mutation<void, TUpdateAnimalAggressiveBehaviorUI>({
      query(body) {
        return {
          url: `${animalUrl}/${EAnimalUrl.AGGRESSIVE_BEHAVIOR}`,
          method: 'PATCH',
          body: updateAnimalAggressiveBehaviorConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalTrainedFor: build.mutation<void, TUpdateAnimalTrainedForUI>({
      query(body) {
        return {
          url: `${animalUrl}/${EAnimalUrl.TRAINED_FOR}`,
          method: 'PATCH',
          body: updateAnimalTrainedForConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalColors: build.mutation<void, TUpdateAnimalColorsUI>({
      query(body) {
        return {
          url: `${animalUrl}/${EAnimalUrl.COLORS}`,
          method: 'PATCH',
          body: updateAnimalColorsConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateAnimalProfilePicture: build.mutation<void, TUpdateAnimalProfilePictureUI>({
      query(body) {
        return {
          url: `${animalUrl}/${EAnimalUrl.PHOTO}`,
          method: 'PATCH',
          body: updateAnimalProfilePictureConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    attachVetRecord: build.mutation<void, TAnimalVetRecordCreateUi>({
      query(body) {
        return {
          url: `${animalUrl}/${EAnimalUrl.ATTACH_VET_RECORD}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['animal'],
    }),
    updateVetRecord: build.mutation<void, TVetRecordDocumentsUI>({
      query(body) {
        return {
          url: `${animalUrl}/${EAnimalUrl.VET_RECORD}`,
          method: 'PUT',
          body: updateVaccinationRecordsConverter.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    deleteVetRecord: build.mutation<void, TVetRecordDeleteUI>({
      query(body) {
        return {
          url: `${animalUrl}/${EAnimalUrl.VET_RECORD}`,
          method: 'DELETE',
          body: deleteVetRecordConvertor.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    getPetNotChargedCount: build.query<{ count: number | null }, void>({
      query() {
        return {
          url: `${animalUrl}/${EAnimalUrl.STATISTICS}/${EAnimalUrl.DASHBOARD}/${EAnimalUrl.NOT_CHARGED_PETS}`,
          method: 'GET',
        };
      },
      providesTags: ['dashboardStatisticsNotChargedPets'],
    }),
    residentAnimalScreening: build.mutation<string, TResidentAnimalScreeningUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EAnimalUrl.ANIMALS}/${EAnimalUrl.SCREENING}`,
          method: 'PATCH',
          body: residentAnimalScreening.toDb(body),
        };
      },
      invalidatesTags: ['animal'],
    }),
    conditionalMovedInApprove: build.mutation<string, TConditionalMovedInApprovedUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EAnimalUrl.ANIMALS}/${EAnimalUrl.CONDITIONAL_MOVED_IN}/${EAnimalUrl.APPROVE}`,
          method: 'PATCH',
          body: conditionalMovedInApproveConverter.toDb(body),
        };
      },
      invalidatesTags: ['animal', 'animals', 'addedPets'],
    }),
    downloadAnimalDocuments: build.query<string, TDownloadAnimalDocumnetsUI>({
      query(params) {
        return {
          url: `${EPropertyMangerUrl.PROPERTY_MANAGER}/${EResidenceUrl.ANIMALS}/${EResidenceUrl.DOCUMENTS_DOWNLOAD}`,
          method: 'GET',
          params: downloadAnimalDocumnetsParamsConverter.toDb(params),
          responseHandler: async (response) => {
            if (response.status === 200) {
              const fileName = `${params.animalName || 'animal'}_documents`;
              const file = await response.blob();
              downloadFile(file, fileName, 'pdf');
              return null;
            }
            const error = await response.json();
            toast({
              description: error.message || EText.DEFAULT_ERROR,
              type: 'error',
            });
            return null;
          },
        };
      },
    }),
    createIncompleteAnimal: build.mutation<TCreateAnimalIncompleteResponseUI, TCreateAnimalIncompleteUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EResidenceUrl.ANIMAL}/${EResidenceUrl.INCOMPLETE}`,
          method: 'POST',
          body: incompleteAnimalConvertor.toDb(body),
        };
      },
      transformResponse: (data: TCreateAnimalIncompleteResponseDB) => {
        return incompleteAnimalResponseConvertor.fromDb(data);
      },
      invalidatesTags: ['animal', 'animals'],
    }),
    completeAnimal: build.mutation<void, TCompleteAnimalUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EResidenceUrl.ANIMAL}/${EResidenceUrl.COMPLETE}`,
          method: 'PUT',
          body: completeAnimalConverter.toDb(body),
        };
      },
      invalidatesTags: ['animal', 'animals'],
    }),
    // Which status actions this animal may be offered, already filtered to what a property
    // manager is allowed. Answered off the application the animal would be given if it has none,
    // so a known animal living under a standing approval is not left with an empty header.
    getAllowedAnimalActions: build.query<TAllowedAnimalActionsUI, TAllowedAnimalActionsParamsUI>({
      query({ tenantResidencyId, animalId }) {
        return {
          url: `${ECommonUrl.COMMON}/${ECommonUrl.TENANT_RESIDENCIES}/${ECommonUrl.ALLOWED_ANIMAL_STATUS_UPDATE_ACTIONS}`,
          method: 'GET',
          params: { tenantResidencyId, animalId },
        };
      },
      providesTags: ['animal'],
      transformResponse: (data: TAllowedAnimalActionsDB) => allowedAnimalActionsConverter.fromDb(data),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPetNotChargedCountQuery,
  useGetAllAnimalsQuery,
  useGetAnimalQuery,
  useGetAnimalStatisticsQuery,
  useTerminateAnimalMutation,
  useLazyAllAnimalsExportCSVQuery,
  useLazyCheckDuplicateAnimalQuery,
  useGetAnimalSpeciesQuery,
  useGetAnimalColorsQuery,
  useGetAnimalBreedQuery,
  useCreateAniamlMutation,
  useUpdateAnimalNameMutation,
  useUpdateAnimalSpeciesMutation,
  useUpdateAnimalWeightMutation,
  useUpdateAnimalBirthDateMutation,
  useUpdateAnimalMicrochipDateMutation,
  useUpdateAnimalReproductiveMutation,
  useUpdateAnimalHouseTrainedMutation,
  useUpdateAnimalGenderMutation,
  useUpdateAnimalBreedMutation,
  useUpdateAnimalAggressiveBehaviorMutation,
  useUpdateAnimalTrainedForMutation,
  useUpdateAnimalColorsMutation,
  useUpdateAnimalProfilePictureMutation,
  useAttachVetRecordMutation,
  useUpdateVetRecordMutation,
  useDeleteVetRecordMutation,
  useResidentAnimalScreeningMutation,
  useConditionalMovedInApproveMutation,
  useLazyDownloadAnimalDocumentsQuery,
  useCreateIncompleteAnimalMutation,
  useCompleteAnimalMutation,
  useGetAllowedAnimalActionsQuery,
} = animalApi;
